import React, { Fragment, useState, useCallback, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Dialog, Transition, Popover } from '@headlessui/react';
import { PlusIcon, XMarkIcon, ArrowPathIcon, ExclamationCircleIcon, CheckCircleIcon, PencilSquareIcon, BookOpenIcon, ClockIcon, LightBulbIcon, CalendarIcon, ChatBubbleLeftRightIcon, PaperAirplaneIcon, ChevronDownIcon, ChevronRightIcon, PhotoIcon, DocumentIcon, BoltIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { apiClient } from '../../api/apiConfig';
import API_BASE from '../../apiBase';
import { useTaskRefresh } from '../../contexts/TaskRefreshContext';
import MentionInput from './MentionInput';
import JournalForm from '../forms/JournalForm';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS, Z_INDEX } from '../../design/tokens';
import { parseDateTimeLocal, toApiDateTime } from '../../utils/timezone';

// Define moods and their corresponding representations
const moodMap: { [key: number]: { emoji: string, color: string, label: string } } = {
  1: { emoji: '😞', color: COLORS.status.error, label: 'Bad' },
  2: { emoji: '😐', color: COLORS.status.warning, label: 'Okay' },
  3: { emoji: '😊', color: COLORS.status.success, label: 'Good' },
};

// Define energy levels and their corresponding representations (1-3 scale)
const energyMap: { [key: number]: { emoji: string, color: string, label: string } } = {
  1: { emoji: '😔', color: COLORS.status.error, label: 'Low' },
  2: { emoji: '😐', color: COLORS.status.warning, label: 'Medium' },
  3: { emoji: '⚡', color: COLORS.status.success, label: 'High' },
};

// Define mental clarity levels and their corresponding representations
const mentalClarityMap: { [key: number]: { emoji: string, color: string, label: string } } = {
  1: { emoji: '🌫️', color: COLORS.text.secondary, label: 'Foggy' },
  2: { emoji: '🌤️', color: COLORS.status.warning, label: 'Clear' },
  3: { emoji: '🧠', color: COLORS.accent.default, label: 'Sharp' },
};

// Define irritability levels and their corresponding representations
// 1=Irritable (bad, bottom of chart), 2=Neutral, 3=Calm (good, top of chart)
const irritabilityMap: { [key: number]: { emoji: string, color: string, label: string } } = {
  1: { emoji: '😠', color: COLORS.status.error, label: 'Irritable' },
  2: { emoji: '😐', color: COLORS.status.warning, label: 'Neutral' },
  3: { emoji: '😌', color: COLORS.status.success, label: 'Calm' },
};

// Chat interfaces
interface ChatConversation {
  id: number;
  title: string;
}

interface ChatToolCall {
  tool_name: string;
  tool_input: Record<string, unknown>;
  tool_use_id: string;
  needs_confirmation?: boolean;
}

interface ChatToolResult {
  tool_name: string;
  result: string;
  tool_use_id: string;
}

interface ChatMessage {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  tool_calls: ChatToolCall[];
  tool_results: ChatToolResult[];
  model?: string;
}

const getModelLabel = (model?: string): { name: string; color: string } | null => {
  if (!model) return null;
  if (model.includes('haiku')) return { name: 'Haiku', color: '#10B981' };
  if (model.includes('sonnet')) return { name: 'Sonnet', color: '#6366F1' };
  if (model.includes('opus')) return { name: 'Opus', color: '#F59E0B' };
  return null;
};

// Compact markdown components for the floating chat panel
const chatMdComponents: Record<string, React.FC<Record<string, unknown>>> = {
  code: ({ inline, children, ...props }: { inline?: boolean; children?: React.ReactNode; [key: string]: unknown }) => {
    if (inline) {
      return <code style={{ backgroundColor: 'rgba(0,0,0,0.08)', padding: '1px 4px', borderRadius: 3, fontFamily: 'monospace', fontSize: '0.85em' }} {...props}>{children}</code>;
    }
    return <code style={{ fontFamily: 'monospace', fontSize: '0.85em' }} {...props}>{children}</code>;
  },
  pre: ({ children, ...props }: { children?: React.ReactNode; [key: string]: unknown }) => (
    <pre style={{ backgroundColor: 'rgba(0,0,0,0.06)', padding: SPACING[8], borderRadius: RADIUS, overflowX: 'auto', fontFamily: 'monospace', fontSize: '0.8em', lineHeight: '1.4', margin: `${SPACING[4]} 0` }} {...props}>{children}</pre>
  ),
  table: ({ children, ...props }: { children?: React.ReactNode; [key: string]: unknown }) => (
    <table style={{ borderCollapse: 'collapse', width: '100%', margin: `${SPACING[4]} 0`, fontSize: '0.85em' }} {...props}>{children}</table>
  ),
  th: ({ children, ...props }: { children?: React.ReactNode; [key: string]: unknown }) => (
    <th style={{ border: `1px solid ${COLORS.border.default}`, padding: `2px ${SPACING[4]}`, backgroundColor: 'rgba(0,0,0,0.04)', textAlign: 'left', fontWeight: 600 }} {...props}>{children}</th>
  ),
  td: ({ children, ...props }: { children?: React.ReactNode; [key: string]: unknown }) => (
    <td style={{ border: `1px solid ${COLORS.border.default}`, padding: `2px ${SPACING[4]}` }} {...props}>{children}</td>
  ),
  a: ({ children, href, ...props }: { children?: React.ReactNode; href?: string; [key: string]: unknown }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: COLORS.accent.default, textDecoration: 'underline' }} {...props}>{children}</a>
  ),
  ul: ({ children, ...props }: { children?: React.ReactNode; [key: string]: unknown }) => (
    <ul style={{ paddingLeft: SPACING[16], margin: `${SPACING[4]} 0` }} {...props}>{children}</ul>
  ),
  ol: ({ children, ...props }: { children?: React.ReactNode; [key: string]: unknown }) => (
    <ol style={{ paddingLeft: SPACING[16], margin: `${SPACING[4]} 0` }} {...props}>{children}</ol>
  ),
  li: ({ children, ...props }: { children?: React.ReactNode; [key: string]: unknown }) => (
    <li style={{ marginBottom: 2 }} {...props}>{children}</li>
  ),
  blockquote: ({ children, ...props }: { children?: React.ReactNode; [key: string]: unknown }) => (
    <blockquote style={{ borderLeft: `2px solid ${COLORS.accent.default}`, paddingLeft: SPACING[8], margin: `${SPACING[4]} 0`, color: COLORS.text.secondary }} {...props}>{children}</blockquote>
  ),
  p: ({ children, ...props }: { children?: React.ReactNode; [key: string]: unknown }) => (
    <p style={{ margin: `${SPACING[4]} 0` }} {...props}>{children}</p>
  ),
};

const FloatingActionMenu: React.FC = () => {
  const { refreshTasks } = useTaskRefresh();

  // Modal States
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [isJournalModalOpen, setIsJournalModalOpen] = useState(false);
  const [isWellnessModalOpen, setIsWellnessModalOpen] = useState(false); // Aggregated wellness modal
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);

  // Task Modal State
  const [quickTaskInput, setQuickTaskInput] = useState('');
  const [isTopPriority, setIsTopPriority] = useState(false);
  const [isSubmittingTask, setIsSubmittingTask] = useState(false);
  const [submitTaskError, setSubmitTaskError] = useState<string | null>(null);
  const [submitTaskSuccess, setSubmitTaskSuccess] = useState(false);

  // Journal Modal State - simplified to just track open/close
  // Actual form logic handled by JournalForm component

  // Aggregated Wellness Modal State
  const [wellnessMood, setWellnessMood] = useState<number | null>(null);
  const [wellnessEnergy, setWellnessEnergy] = useState<number | null>(null);
  const [wellnessMentalClarity, setWellnessMentalClarity] = useState<number | null>(null);
  const [wellnessIrritability, setWellnessIrritability] = useState<number | null>(null);
  const [wellnessComment, setWellnessComment] = useState('');
  const [isSubmittingWellness, setIsSubmittingWellness] = useState(false);
  const [submitWellnessError, setSubmitWellnessError] = useState<string | null>(null);
  const [submitWellnessSuccess, setSubmitWellnessSuccess] = useState(false);

  // Event Modal State
  const [eventTitle, setEventTitle] = useState('');
  const [eventDescription, setEventDescription] = useState('');
  const [eventStart, setEventStart] = useState('');
  const [eventEnd, setEventEnd] = useState('');
  const [eventAllDay, setEventAllDay] = useState(false);
  const [isSubmittingEvent, setIsSubmittingEvent] = useState(false);
  const [submitEventError, setSubmitEventError] = useState<string | null>(null);
  const [submitEventSuccess, setSubmitEventSuccess] = useState(false);

  // New state for selected person IDs
  const [selectedTaskPersonIds, setSelectedTaskPersonIds] = useState<number[]>([]);

  // Chat Panel State
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [showChatChoice, setShowChatChoice] = useState(false);
  const [lastConversation, setLastConversation] = useState<ChatConversation | null>(null);
  const [chatConversation, setChatConversation] = useState<ChatConversation | null>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatFabHovered, setChatFabHovered] = useState(false);
  const [chatPendingConfirmations, setChatPendingConfirmations] = useState<ChatToolCall[]>([]);
  const [chatAgentMode, setChatAgentMode] = useState(false);
  const [chatStreamingContent, setChatStreamingContent] = useState('');
  const [chatIsStreaming, setChatIsStreaming] = useState(false);
  const [chatStreamingModel, setChatStreamingModel] = useState('');
  const [chatVerification, setChatVerification] = useState<{verified: boolean; issues: string[]} | null>(null);
  const chatContentRef = useRef('');
  const chatMessagesEndRef = useRef<HTMLDivElement>(null);

  // Hover states for interactive elements
  const [fabHovered, setFabHovered] = useState(false);
  const [hoveredMenuItem, setHoveredMenuItem] = useState<string | null>(null);
  const [submitTaskHovered, setSubmitTaskHovered] = useState(false);
  const [submitWellnessHovered, setSubmitWellnessHovered] = useState(false);
  const [cancelWellnessHovered, setCancelWellnessHovered] = useState(false);
  const [submitEventHovered, setSubmitEventHovered] = useState(false);
  const [closeTaskHovered, setCloseTaskHovered] = useState(false);
  const [closeEventHovered, setCloseEventHovered] = useState(false);

  // --- Task Modal Logic ---
  const openTaskModal = () => setIsTaskModalOpen(true);
  const closeTaskModal = useCallback(() => {
    if (isSubmittingTask) return;
    setIsTaskModalOpen(false);
    setTimeout(() => {
        setQuickTaskInput('');
        setIsTopPriority(false);
        setSubmitTaskError(null);
        setSubmitTaskSuccess(false);
    }, 300);
  }, [isSubmittingTask]);

  const handleTaskSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!quickTaskInput.trim()) return;
    setIsSubmittingTask(true);
    setSubmitTaskError(null);
    setSubmitTaskSuccess(false);
    try {
      const response = await apiClient.post('/api/quick_task/', {
        text: quickTaskInput,
        responsible_ids: selectedTaskPersonIds,
        impacted_ids: selectedTaskPersonIds,
        is_top_priority: isTopPriority
      });

      if (response.status >= 200 && response.status < 300) {
        setSubmitTaskSuccess(true);
        setQuickTaskInput('');
        setIsTopPriority(false);
        setSelectedTaskPersonIds([]);
        refreshTasks();
        setTimeout(() => { closeTaskModal(); }, 1000);
      } else {
        throw new Error(`Failed to create task (${response.status})`);
      }
    } catch (err: any) {
      const errorMsg = err.message || 'Failed to create task.';
      setSubmitTaskError(errorMsg);
    } finally {
      setIsSubmittingTask(false);
    }
  };
  // --- End Task Modal Logic ---

  // --- Journal Modal Logic ---
  const openJournalModal = () => setIsJournalModalOpen(true);
  const closeJournalModal = () => setIsJournalModalOpen(false);
  const handleJournalCreated = () => {
    // Optional: Add any refresh logic here if needed
  };
  // --- End Journal Modal Logic ---

  // --- Wellness Modal Logic ---
  const openWellnessModal = () => setIsWellnessModalOpen(true);
  const closeWellnessModal = useCallback(() => {
    if (isSubmittingWellness) return;
    setIsWellnessModalOpen(false);
    setTimeout(() => {
      setWellnessMood(null);
      setWellnessEnergy(null);
      setWellnessMentalClarity(null);
      setWellnessIrritability(null);
      setWellnessComment('');
      setSubmitWellnessError(null);
      setSubmitWellnessSuccess(false);
    }, 300);
  }, [isSubmittingWellness]);

  const handleWellnessSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check if at least one indicator is selected
    if (wellnessMood === null && wellnessEnergy === null && wellnessMentalClarity === null && wellnessIrritability === null) {
      setSubmitWellnessError("Please select at least one wellness indicator.");
      return;
    }

    setIsSubmittingWellness(true);
    setSubmitWellnessError(null);
    setSubmitWellnessSuccess(false);

    try {
      const promises = [];

      // Submit mood if selected
      if (wellnessMood !== null) {
        promises.push(
          apiClient.post('/api/moods/', {
            level: wellnessMood,
            comment: wellnessComment.trim() || null
          })
        );
      }

      // Submit energy if selected
      if (wellnessEnergy !== null) {
        promises.push(
          apiClient.post('/api/energy/', {
            level: wellnessEnergy,
            comment: wellnessComment.trim() || null
          })
        );
      }

      // Submit mental clarity if selected
      if (wellnessMentalClarity !== null) {
        promises.push(
          apiClient.post('/api/mental-clarity/', {
            level: wellnessMentalClarity,
            comment: wellnessComment.trim() || null
          })
        );
      }

      // Submit irritability if selected
      if (wellnessIrritability !== null) {
        promises.push(
          apiClient.post('/api/irritability/', {
            level: wellnessIrritability,
            comment: wellnessComment.trim() || null
          })
        );
      }

      // Wait for all submissions to complete
      await Promise.all(promises);

      setSubmitWellnessSuccess(true);

      // Trigger refresh of all trackers
      window.dispatchEvent(new CustomEvent('moodDataUpdated'));
      window.dispatchEvent(new CustomEvent('energyDataUpdated'));
      window.dispatchEvent(new CustomEvent('mentalClarityDataUpdated'));
      window.dispatchEvent(new CustomEvent('irritabilityDataUpdated'));

      setTimeout(() => { closeWellnessModal(); }, 1500);

    } catch (err: any) {
      console.error('Wellness submission error:', err);
      const errorMsg = err.message || 'Failed to track wellness data.';
      setSubmitWellnessError(errorMsg);
    } finally {
      setIsSubmittingWellness(false);
    }
  };
  // --- End Wellness Modal Logic ---

  // --- Event Modal Logic ---
  const openEventModal = () => {
    // Set default times (current time for start, 1 hour later for end)
    const now = new Date();
    const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000);

    const formatDateTime = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    setEventStart(formatDateTime(now));
    setEventEnd(formatDateTime(oneHourLater));
    setIsEventModalOpen(true);
  };

  const closeEventModal = useCallback(() => {
    if (isSubmittingEvent) return;
    setIsEventModalOpen(false);
    setTimeout(() => {
      setEventTitle('');
      setEventDescription('');
      setEventStart('');
      setEventEnd('');
      setEventAllDay(false);
      setSubmitEventError(null);
      setSubmitEventSuccess(false);
    }, 300);
  }, [isSubmittingEvent]);

  // Auto-update end time when start time changes (maintain 1-hour duration)
  const handleStartTimeChange = (newStart: string) => {
    setEventStart(newStart);

    // Parse the new start time and add 1 hour for end time
    if (newStart) {
      const startDate = parseDateTimeLocal(newStart);
      const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // +1 hour

      // Format back to datetime-local format
      const year = endDate.getFullYear();
      const month = String(endDate.getMonth() + 1).padStart(2, '0');
      const day = String(endDate.getDate()).padStart(2, '0');
      const hours = String(endDate.getHours()).padStart(2, '0');
      const minutes = String(endDate.getMinutes()).padStart(2, '0');

      setEventEnd(`${year}-${month}-${day}T${hours}:${minutes}`);
    }
  };

  const handleEventSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!eventTitle.trim()) {
      setSubmitEventError('Event title is required');
      return;
    }
    if (!eventStart) {
      setSubmitEventError('Start time is required');
      return;
    }
    if (!eventEnd) {
      setSubmitEventError('End time is required');
      return;
    }

    setIsSubmittingEvent(true);
    setSubmitEventError(null);
    setSubmitEventSuccess(false);

    try {
      // Convert local time from datetime-local input to UTC for API
      const startDate = parseDateTimeLocal(eventStart);
      const endDate = parseDateTimeLocal(eventEnd);

      const response = await apiClient.post('/api/calendar_events/', {
        title: eventTitle.trim(),
        description: eventDescription.trim(),
        start: toApiDateTime(startDate),
        end: toApiDateTime(endDate),
        all_day: eventAllDay,
        event_type: 'event'
      });

      if (response.status >= 200 && response.status < 300) {
        setSubmitEventSuccess(true);
        setTimeout(() => { closeEventModal(); }, 1500);
      } else {
        throw new Error(`Failed to create event (${response.status})`);
      }
    } catch (err: any) {
      console.error('Event submission error:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to create event.';
      setSubmitEventError(errorMsg);
    } finally {
      setIsSubmittingEvent(false);
    }
  };
  // --- End Event Modal Logic ---

  // --- Chat Panel Logic ---
  const scrollChatToBottom = () => {
    chatMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollChatToBottom();
  }, [chatMessages, chatStreamingContent]);

  const openChatPanel = async () => {
    // If we already have an active conversation in this session, just open
    if (chatConversation) {
      setIsChatOpen(true);
      return;
    }

    // Check if there's a recent conversation to resume (one with messages)
    try {
      const res = await apiClient.get('/api/chat/conversations/');
      const conversations = res.data;
      // Find the first conversation with messages
      const conversationWithMessages = conversations?.find((c: ChatConversation & { message_count?: number }) => c.message_count && c.message_count > 0);
      if (conversationWithMessages) {
        // There's a previous conversation with messages - show choice
        setLastConversation(conversationWithMessages);
        setShowChatChoice(true);
        setIsChatOpen(true);
      } else {
        // No previous conversations with messages - open empty chat, created on first send
        setIsChatOpen(true);
        setChatConversation(null);
        setChatMessages([]);
      }
    } catch (err) {
      console.error('Failed to fetch conversations', err);
      setIsChatOpen(true);
    }
  };

  const resumeLastChat = async () => {
    if (!lastConversation) return;
    setShowChatChoice(false);
    setChatConversation(lastConversation);
    // Load messages
    try {
      const res = await apiClient.get(`/api/chat/conversations/${lastConversation.id}/messages/`);
      setChatMessages(res.data);
    } catch (err) {
      console.error('Failed to load messages', err);
    }
  };

  const startNewChatFromChoice = () => {
    setShowChatChoice(false);
    setChatConversation(null);
    setChatMessages([]);
  };

  const closeChatPanel = () => {
    setIsChatOpen(false);
    setShowChatChoice(false);
  };

  const sendChatMessage = async (confirmedToolIds?: Record<string, boolean>) => {
    const content = chatInput.trim();
    if (!content && !confirmedToolIds) return;

    // Lazy-create conversation on first message
    let conversation = chatConversation;
    if (!conversation) {
      try {
        const res = await apiClient.post('/api/chat/conversations/', { title: 'New Chat' });
        conversation = res.data;
        setChatConversation(res.data);
      } catch (err) {
        console.error('Failed to create conversation', err);
        return;
      }
    }

    if (content) {
      setChatMessages(prev => [...prev, {
        id: Date.now(),
        role: 'user',
        content,
        tool_calls: [],
        tool_results: [],
      }]);
      setChatInput('');
    }
    setChatLoading(true);
    setChatIsStreaming(true);
    setChatPendingConfirmations([]);
    setChatStreamingContent('');
    setChatStreamingModel('');
    setChatVerification(null);
    chatContentRef.current = '';

    try {
      const token = localStorage.getItem('floos_access_token');
      const response = await fetch(`${API_BASE}/api/chat/conversations/${conversation!.id}/stream/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          content: content || '',
          confirmed_tool_ids: confirmedToolIds || {},
          agent_mode: chatAgentMode,
        }),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let finalModel = '';
      let gotDone = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const jsonStr = line.slice(6).trim();
          if (!jsonStr) continue;

          try {
            const parsed = JSON.parse(jsonStr);
            const event = parsed.event;

            if (event === 'model') {
              finalModel = parsed.model || '';
              setChatStreamingModel(finalModel);
            } else if (event === 'text_delta') {
              chatContentRef.current += parsed.content || '';
              setChatStreamingContent(chatContentRef.current);
            } else if (event === 'verification') {
              setChatVerification(parsed);
            } else if (event === 'confirmation_needed') {
              gotDone = true;
              setChatMessages(prev => [...prev, {
                id: Date.now(),
                role: 'assistant',
                content: parsed.content || chatContentRef.current,
                tool_calls: parsed.tool_calls || [],
                tool_results: parsed.tool_results || [],
                model: parsed.model || finalModel,
              }]);
              setChatPendingConfirmations(parsed.pending_tool_calls || []);
            } else if (event === 'done') {
              gotDone = true;
              if (parsed.verification) setChatVerification(parsed.verification);
              setChatMessages(prev => [...prev, {
                id: Date.now(),
                role: 'assistant',
                content: parsed.content || chatContentRef.current,
                tool_calls: parsed.tool_calls || [],
                tool_results: parsed.tool_results || [],
                model: parsed.model || finalModel,
              }]);
            }
          } catch {
            // ignore parse errors
          }
        }
      }

      if (!gotDone && chatContentRef.current) {
        setChatMessages(prev => [...prev, {
          id: Date.now(),
          role: 'assistant',
          content: chatContentRef.current,
          tool_calls: [],
          tool_results: [],
          model: finalModel,
        }]);
      }
    } catch (err) {
      console.error('Failed to send chat message', err);
      setChatMessages(prev => [...prev, {
        id: Date.now(),
        role: 'assistant',
        content: 'Sorry, there was an error processing your message.',
        tool_calls: [],
        tool_results: [],
      }]);
    } finally {
      setChatLoading(false);
      setChatIsStreaming(false);
      setChatStreamingContent('');
      chatContentRef.current = '';
    }
  };

  const handleChatConfirmAll = () => {
    const confirmed: Record<string, boolean> = {};
    chatPendingConfirmations.forEach(tc => {
      confirmed[tc.tool_use_id] = true;
    });
    sendChatMessage(confirmed);
  };

  const handleChatReject = () => {
    setChatPendingConfirmations([]);
  };

  const handleChatKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendChatMessage();
    }
  };

  const startNewChat = () => {
    setChatConversation(null);
    setChatMessages([]);
  };
  // --- End Chat Panel Logic ---

  // Style helpers
  const getMenuItemStyle = (itemKey: string): React.CSSProperties => ({
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    padding: `${SPACING[8]} ${SPACING[16]}`,
    fontSize: TYPOGRAPHY.size.sm,
    color: hoveredMenuItem === itemKey ? COLORS.text.primary : COLORS.text.secondary,
    backgroundColor: hoveredMenuItem === itemKey ? COLORS.bg.hover : 'transparent',
    border: 'none',
    cursor: 'pointer',
    textDecoration: 'none',
  });

  const getWellnessButtonStyle = (isSelected: boolean): React.CSSProperties => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: SPACING[12],
    borderRadius: RADIUS,
    border: `2px solid ${isSelected ? COLORS.accent.default : COLORS.border.default}`,
    backgroundColor: isSelected ? '#EBF5FF' : 'transparent',
    cursor: 'pointer',
    transition: 'all 150ms ease',
  });

  const getEnergyButtonStyle = (isSelected: boolean): React.CSSProperties => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: SPACING[8],
    borderRadius: RADIUS,
    border: `2px solid ${isSelected ? COLORS.accent.default : COLORS.border.default}`,
    backgroundColor: isSelected ? '#EBF5FF' : 'transparent',
    cursor: 'pointer',
    transition: 'all 150ms ease',
  });

  return (
    <>
      {/* Main FAB using Popover */}
      <Popover style={{ position: 'fixed', bottom: SPACING[24], right: SPACING[24], zIndex: Z_INDEX.dropdown }}>
        {({ open, close }) => (
          <>
            <Popover.Button
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '56px',
                height: '56px',
                padding: SPACING[12],
                border: 'none',
                borderRadius: '50%',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                color: COLORS.bg.panel,
                backgroundColor: fabHovered ? COLORS.accent.hover : COLORS.accent.default,
                cursor: 'pointer',
                outline: 'none',
                transition: 'transform 150ms ease, background-color 150ms ease',
                transform: open ? 'rotate(45deg)' : (fabHovered ? 'scale(1.1)' : 'scale(1)'),
              }}
              onMouseEnter={() => setFabHovered(true)}
              onMouseLeave={() => setFabHovered(false)}
              aria-label="Quick Add Menu"
            >
              <PlusIcon style={{ height: '28px', width: '28px' }} aria-hidden="true" />
            </Popover.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95 -translate-y-2"
              enterTo="transform opacity-100 scale-100 translate-y-0"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100 translate-y-0"
              leaveTo="transform opacity-0 scale-95 -translate-y-2"
            >
              <Popover.Panel
                style={{
                  position: 'absolute',
                  bottom: '100%',
                  right: 0,
                  marginBottom: SPACING[8],
                  width: '192px',
                  transformOrigin: 'bottom right',
                  borderRadius: RADIUS,
                  backgroundColor: COLORS.bg.panel,
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                  outline: 'none',
                }}
              >
                <div style={{ padding: `${SPACING[4]} 0` }}>
                  <button
                    onClick={() => { openTaskModal(); close(); }}
                    style={getMenuItemStyle('task')}
                    onMouseEnter={() => setHoveredMenuItem('task')}
                    onMouseLeave={() => setHoveredMenuItem(null)}
                  >
                    <PencilSquareIcon style={{ marginRight: SPACING[12], height: '20px', width: '20px', color: COLORS.text.tertiary }} aria-hidden="true" />
                    Quick Task
                  </button>
                  <button
                    onClick={() => { openJournalModal(); close(); }}
                    style={getMenuItemStyle('journal')}
                    onMouseEnter={() => setHoveredMenuItem('journal')}
                    onMouseLeave={() => setHoveredMenuItem(null)}
                  >
                    <BookOpenIcon style={{ marginRight: SPACING[12], height: '20px', width: '20px', color: COLORS.text.tertiary }} aria-hidden="true" />
                    Journal
                  </button>
                  {/* Create Event Button */}
                  <button
                    onClick={() => { openEventModal(); close(); }}
                    style={getMenuItemStyle('event')}
                    onMouseEnter={() => setHoveredMenuItem('event')}
                    onMouseLeave={() => setHoveredMenuItem(null)}
                  >
                    <CalendarIcon style={{ marginRight: SPACING[12], height: '20px', width: '20px', color: COLORS.text.tertiary }} aria-hidden="true" />
                    Create Event
                  </button>
                  {/* Wellness Tracker Button */}
                  <button
                    onClick={() => { openWellnessModal(); close(); }}
                    style={getMenuItemStyle('wellness')}
                    onMouseEnter={() => setHoveredMenuItem('wellness')}
                    onMouseLeave={() => setHoveredMenuItem(null)}
                  >
                    <LightBulbIcon style={{ marginRight: SPACING[12], height: '20px', width: '20px', color: COLORS.text.tertiary }} aria-hidden="true" />
                    Track Wellness
                  </button>
                  <Link
                    to="/habits"
                    onClick={() => close()}
                    style={getMenuItemStyle('habits')}
                    onMouseEnter={() => setHoveredMenuItem('habits')}
                    onMouseLeave={() => setHoveredMenuItem(null)}
                  >
                    <ClockIcon style={{ marginRight: SPACING[12], height: '20px', width: '20px', color: COLORS.text.tertiary }} aria-hidden="true" />
                    Daily Habits
                  </Link>
                  <Link
                    to="/documents?upload=true"
                    onClick={() => close()}
                    style={getMenuItemStyle('document')}
                    onMouseEnter={() => setHoveredMenuItem('document')}
                    onMouseLeave={() => setHoveredMenuItem(null)}
                  >
                    <DocumentIcon style={{ marginRight: SPACING[12], height: '20px', width: '20px', color: COLORS.text.tertiary }} aria-hidden="true" />
                    Add Document
                  </Link>
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>

      {/* Quick Task Modal */}
      <Transition appear show={isTaskModalOpen} as={Fragment}>
        <Dialog as="div" style={{ position: 'relative', zIndex: Z_INDEX.modal }} onClose={closeTaskModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.3)' }} />
          </Transition.Child>
          <div style={{ position: 'fixed', inset: 0, overflowY: 'auto' }}>
            <div style={{ display: 'flex', minHeight: '100%', alignItems: 'flex-end', justifyContent: 'center', padding: SPACING[16], textAlign: 'center' }}>
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95 sm:translate-y-full"
                enterTo="opacity-100 scale-100 sm:translate-y-0"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100 sm:translate-y-0"
                leaveTo="opacity-0 scale-95 sm:translate-y-full"
              >
                <Dialog.Panel
                  style={{
                    width: '100%',
                    maxWidth: '448px',
                    transform: 'none',
                    overflow: 'hidden',
                    borderRadius: '16px',
                    backgroundColor: COLORS.bg.panel,
                    padding: SPACING[24],
                    textAlign: 'left',
                    verticalAlign: 'middle',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                    transition: 'all 150ms ease',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: SPACING[16] }}>
                    <Dialog.Title as="h3" style={{ fontSize: TYPOGRAPHY.size['2xl'], fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.primary }}>Quick Add Task</Dialog.Title>
                    <button
                      type="button"
                      style={{
                        color: closeTaskHovered ? COLORS.text.secondary : COLORS.text.tertiary,
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        opacity: isSubmittingTask ? 0.5 : 1,
                      }}
                      onClick={closeTaskModal}
                      disabled={isSubmittingTask}
                      onMouseEnter={() => setCloseTaskHovered(true)}
                      onMouseLeave={() => setCloseTaskHovered(false)}
                    >
                      <XMarkIcon style={{ height: '24px', width: '24px' }} />
                    </button>
                  </div>
                  <form onSubmit={handleTaskSubmit}>
                    <MentionInput
                      value={quickTaskInput}
                      onChange={(value, personIds) => {
                        setQuickTaskInput(value);
                        setSelectedTaskPersonIds(personIds);
                      }}
                      placeholder="Type @ to mention someone..."
                      rows={3}
                    />
                    <div style={{ marginTop: SPACING[12], display: 'flex', alignItems: 'center' }}>
                      <input
                        id="top-priority-toggle"
                        type="checkbox"
                        checked={isTopPriority}
                        onChange={(e) => setIsTopPriority(e.target.checked)}
                        style={{ height: '16px', width: '16px', borderRadius: '4px', borderColor: COLORS.border.default, accentColor: COLORS.status.error }}
                      />
                      <label htmlFor="top-priority-toggle" style={{ marginLeft: SPACING[8], fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.secondary }}>
                        Mark as <span style={{ fontWeight: TYPOGRAPHY.weight.semibold, color: COLORS.status.error }}>Top Priority</span>
                      </label>
                    </div>
                    <div style={{ marginTop: SPACING[16], display: 'flex', flexDirection: 'column', gap: SPACING[8] }}>
                      {submitTaskSuccess && (
                        <div style={{ padding: SPACING[8], fontSize: TYPOGRAPHY.size.sm, color: '#15803D', backgroundColor: '#F0FDF4', borderRadius: RADIUS, border: '1px solid #BBF7D0', display: 'flex', alignItems: 'center' }}>
                          <CheckCircleIcon style={{ height: '16px', width: '16px', marginRight: SPACING[8] }} />Task submitted!
                        </div>
                      )}
                      {submitTaskError && (
                        <div style={{ padding: SPACING[8], fontSize: TYPOGRAPHY.size.sm, color: '#B91C1C', backgroundColor: '#FEF2F2', borderRadius: RADIUS, border: '1px solid #FECACA', display: 'flex', alignItems: 'center' }}>
                          <ExclamationCircleIcon style={{ height: '16px', width: '16px', marginRight: SPACING[8] }} />{submitTaskError}
                        </div>
                      )}
                      <button
                        type="submit"
                        style={{
                          display: 'inline-flex',
                          width: '100%',
                          justifyContent: 'center',
                          alignItems: 'center',
                          borderRadius: RADIUS,
                          border: 'none',
                          backgroundColor: (isSubmittingTask || !quickTaskInput.trim()) ? COLORS.text.tertiary : (submitTaskHovered ? COLORS.accent.hover : COLORS.accent.default),
                          padding: `${SPACING[8]} ${SPACING[16]}`,
                          fontSize: TYPOGRAPHY.size.sm,
                          fontWeight: TYPOGRAPHY.weight.medium,
                          color: COLORS.bg.panel,
                          cursor: (isSubmittingTask || !quickTaskInput.trim()) ? 'not-allowed' : 'pointer',
                          opacity: (isSubmittingTask || !quickTaskInput.trim()) ? 0.5 : 1,
                        }}
                        disabled={isSubmittingTask || !quickTaskInput.trim()}
                        onMouseEnter={() => setSubmitTaskHovered(true)}
                        onMouseLeave={() => setSubmitTaskHovered(false)}
                      >
                        {isSubmittingTask ? (
                          <>
                            <ArrowPathIcon style={{ width: '16px', height: '16px', marginRight: SPACING[8], animation: 'spin 1s linear infinite' }} />
                            Adding...
                          </>
                        ) : 'Add Task'}
                      </button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Journal Form - Standard form for creating journal entries */}
      <JournalForm
        isOpen={isJournalModalOpen}
        onClose={closeJournalModal}
        onJournalEntryCreated={handleJournalCreated}
      />

      {/* Wellness Modal */}
      <Transition show={isWellnessModalOpen} as={Fragment}>
        <Dialog as="div" style={{ position: 'relative', zIndex: Z_INDEX.modal }} onClose={closeWellnessModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(107, 114, 128, 0.75)', transition: 'opacity 150ms ease' }} />
          </Transition.Child>

          <div style={{ position: 'fixed', inset: 0, zIndex: Z_INDEX.dropdown, overflowY: 'auto' }}>
            <div style={{ display: 'flex', minHeight: '100%', alignItems: 'flex-end', justifyContent: 'center', padding: SPACING[16], textAlign: 'center' }}>
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel
                  style={{
                    position: 'relative',
                    transform: 'none',
                    overflow: 'hidden',
                    borderRadius: RADIUS,
                    backgroundColor: COLORS.bg.panel,
                    padding: `${SPACING[24]} ${SPACING[16]} ${SPACING[16]} ${SPACING[16]}`,
                    textAlign: 'left',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                    transition: 'all 150ms ease',
                    width: '100%',
                    maxWidth: '512px',
                  }}
                >
                  <div>
                    <div style={{ margin: '0 auto', display: 'flex', height: '48px', width: '48px', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', backgroundColor: '#DBEAFE' }}>
                      <LightBulbIcon style={{ height: '24px', width: '24px', color: COLORS.accent.default }} aria-hidden="true" />
                    </div>
                    <div style={{ marginTop: SPACING[12], textAlign: 'center' }}>
                      <Dialog.Title as="h3" style={{ fontSize: TYPOGRAPHY.size['2xl'], fontWeight: TYPOGRAPHY.weight.semibold, lineHeight: '1.5', color: COLORS.text.primary }}>
                        Track Wellness
                      </Dialog.Title>
                      <div style={{ marginTop: SPACING[8] }}>
                        <p style={{ fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.secondary }}>
                          Track your mood, energy, and mental clarity all at once.
                        </p>
                      </div>
                    </div>
                  </div>

                  <form onSubmit={handleWellnessSubmit} style={{ marginTop: SPACING[24], display: 'flex', flexDirection: 'column', gap: SPACING[24] }}>
                    {/* Mood Section */}
                    <div>
                      <label style={{ display: 'block', fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.secondary, marginBottom: SPACING[12] }}>
                        Mood (Optional)
                      </label>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: SPACING[16] }}>
                        {Object.entries(moodMap).map(([level, { emoji, color, label }]) => (
                          <button
                            key={level}
                            type="button"
                            onClick={() => setWellnessMood(wellnessMood === parseInt(level) ? null : parseInt(level))}
                            style={getWellnessButtonStyle(wellnessMood === parseInt(level))}
                          >
                            <span style={{ fontSize: TYPOGRAPHY.size['3xl'], marginBottom: SPACING[4] }}>{emoji}</span>
                            <span style={{ fontSize: TYPOGRAPHY.size.xs, fontWeight: TYPOGRAPHY.weight.medium, color }}>{label}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Energy Section */}
                    <div>
                      <label style={{ display: 'block', fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.secondary, marginBottom: SPACING[12] }}>
                        Energy (Optional)
                      </label>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: SPACING[16] }}>
                        {Object.entries(energyMap).map(([level, { emoji, color, label }]) => (
                          <button
                            key={level}
                            type="button"
                            onClick={() => setWellnessEnergy(wellnessEnergy === parseInt(level) ? null : parseInt(level))}
                            style={getWellnessButtonStyle(wellnessEnergy === parseInt(level))}
                          >
                            <span style={{ fontSize: TYPOGRAPHY.size['3xl'], marginBottom: SPACING[4] }}>{emoji}</span>
                            <span style={{ fontSize: TYPOGRAPHY.size.xs, fontWeight: TYPOGRAPHY.weight.medium, color }}>{label}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Mental Clarity Section */}
                    <div>
                      <label style={{ display: 'block', fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.secondary, marginBottom: SPACING[12] }}>
                        Mental Clarity (Optional)
                      </label>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: SPACING[16] }}>
                        {Object.entries(mentalClarityMap).map(([level, { emoji, color, label }]) => (
                          <button
                            key={level}
                            type="button"
                            onClick={() => setWellnessMentalClarity(wellnessMentalClarity === parseInt(level) ? null : parseInt(level))}
                            style={getWellnessButtonStyle(wellnessMentalClarity === parseInt(level))}
                          >
                            <span style={{ fontSize: TYPOGRAPHY.size['3xl'], marginBottom: SPACING[4] }}>{emoji}</span>
                            <span style={{ fontSize: TYPOGRAPHY.size.xs, fontWeight: TYPOGRAPHY.weight.medium, color }}>{label}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Irritability Section */}
                    <div>
                      <label style={{ display: 'block', fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.secondary, marginBottom: SPACING[12] }}>
                        Irritability (Optional)
                      </label>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: SPACING[16] }}>
                        {Object.entries(irritabilityMap).map(([level, { emoji, color, label }]) => (
                          <button
                            key={level}
                            type="button"
                            onClick={() => setWellnessIrritability(wellnessIrritability === parseInt(level) ? null : parseInt(level))}
                            style={getWellnessButtonStyle(wellnessIrritability === parseInt(level))}
                          >
                            <span style={{ fontSize: TYPOGRAPHY.size['3xl'], marginBottom: SPACING[4] }}>{emoji}</span>
                            <span style={{ fontSize: TYPOGRAPHY.size.xs, fontWeight: TYPOGRAPHY.weight.medium, color }}>{label}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Comment Section */}
                    <div>
                      <label htmlFor="wellness-comment" style={{ display: 'block', fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.secondary }}>
                        Comment (Optional)
                      </label>
                      <textarea
                        id="wellness-comment"
                        rows={3}
                        style={{
                          marginTop: SPACING[4],
                          display: 'block',
                          width: '100%',
                          borderRadius: RADIUS,
                          border: `1px solid ${COLORS.border.default}`,
                          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                          fontSize: TYPOGRAPHY.size.sm,
                          padding: SPACING[8],
                          fontFamily: TYPOGRAPHY.body.fontFamily,
                          resize: 'vertical',
                        }}
                        placeholder="How are you feeling? Any context to add..."
                        value={wellnessComment}
                        onChange={(e) => setWellnessComment(e.target.value)}
                      />
                    </div>

                    {/* Error Display */}
                    {submitWellnessError && (
                      <div style={{ borderRadius: RADIUS, backgroundColor: '#FEF2F2', padding: SPACING[16] }}>
                        <div style={{ display: 'flex' }}>
                          <div style={{ flexShrink: 0 }}>
                            <ExclamationCircleIcon style={{ height: '20px', width: '20px', color: '#F87171' }} aria-hidden="true" />
                          </div>
                          <div style={{ marginLeft: SPACING[12] }}>
                            <h3 style={{ fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: '#991B1B' }}>{submitWellnessError}</h3>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Success Display */}
                    {submitWellnessSuccess && (
                      <div style={{ borderRadius: RADIUS, backgroundColor: '#F0FDF4', padding: SPACING[16] }}>
                        <div style={{ display: 'flex' }}>
                          <div style={{ flexShrink: 0 }}>
                            <CheckCircleIcon style={{ height: '20px', width: '20px', color: '#4ADE80' }} aria-hidden="true" />
                          </div>
                          <div style={{ marginLeft: SPACING[12] }}>
                            <h3 style={{ fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: '#166534' }}>Wellness data tracked successfully!</h3>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div style={{ marginTop: SPACING[24], display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: SPACING[12] }}>
                      <button
                        type="button"
                        style={{
                          display: 'inline-flex',
                          width: '100%',
                          justifyContent: 'center',
                          borderRadius: RADIUS,
                          backgroundColor: cancelWellnessHovered ? COLORS.bg.hover : COLORS.bg.panel,
                          padding: `${SPACING[8]} ${SPACING[12]}`,
                          fontSize: TYPOGRAPHY.size.sm,
                          fontWeight: TYPOGRAPHY.weight.semibold,
                          color: COLORS.text.primary,
                          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                          border: `1px solid ${COLORS.border.default}`,
                          cursor: 'pointer',
                          order: 1,
                        }}
                        onClick={closeWellnessModal}
                        onMouseEnter={() => setCancelWellnessHovered(true)}
                        onMouseLeave={() => setCancelWellnessHovered(false)}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmittingWellness}
                        style={{
                          display: 'inline-flex',
                          width: '100%',
                          justifyContent: 'center',
                          alignItems: 'center',
                          borderRadius: RADIUS,
                          backgroundColor: isSubmittingWellness ? COLORS.text.tertiary : (submitWellnessHovered ? COLORS.accent.hover : COLORS.accent.default),
                          padding: `${SPACING[8]} ${SPACING[12]}`,
                          fontSize: TYPOGRAPHY.size.sm,
                          fontWeight: TYPOGRAPHY.weight.semibold,
                          color: COLORS.bg.panel,
                          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                          border: 'none',
                          cursor: isSubmittingWellness ? 'not-allowed' : 'pointer',
                          opacity: isSubmittingWellness ? 0.5 : 1,
                          order: 2,
                        }}
                        onMouseEnter={() => setSubmitWellnessHovered(true)}
                        onMouseLeave={() => setSubmitWellnessHovered(false)}
                      >
                        {isSubmittingWellness ? (
                          <>
                            <ArrowPathIcon style={{ height: '16px', width: '16px', marginRight: SPACING[8], animation: 'spin 1s linear infinite' }} />
                            Tracking...
                          </>
                        ) : (
                          'Track Wellness'
                        )}
                      </button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Create Event Modal */}
      <Transition appear show={isEventModalOpen} as={Fragment}>
        <Dialog as="div" style={{ position: 'relative', zIndex: Z_INDEX.modal }} onClose={closeEventModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.3)' }} />
          </Transition.Child>

          <div style={{ position: 'fixed', inset: 0, overflowY: 'auto' }}>
            <div style={{ display: 'flex', minHeight: '100%', alignItems: 'flex-end', justifyContent: 'center', padding: SPACING[16], textAlign: 'center' }}>
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95 sm:translate-y-full"
                enterTo="opacity-100 scale-100 sm:translate-y-0"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100 sm:translate-y-0"
                leaveTo="opacity-0 scale-95 sm:translate-y-full"
              >
                <Dialog.Panel
                  style={{
                    width: '100%',
                    maxWidth: '448px',
                    transform: 'none',
                    overflow: 'hidden',
                    borderRadius: '16px',
                    backgroundColor: COLORS.bg.panel,
                    padding: SPACING[24],
                    textAlign: 'left',
                    verticalAlign: 'middle',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                    transition: 'all 150ms ease',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: SPACING[16] }}>
                    <Dialog.Title as="h3" style={{ fontSize: TYPOGRAPHY.size['2xl'], fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.primary }}>Create Event</Dialog.Title>
                    <button
                      type="button"
                      style={{
                        color: closeEventHovered ? COLORS.text.secondary : COLORS.text.tertiary,
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        opacity: isSubmittingEvent ? 0.5 : 1,
                      }}
                      onClick={closeEventModal}
                      disabled={isSubmittingEvent}
                      onMouseEnter={() => setCloseEventHovered(true)}
                      onMouseLeave={() => setCloseEventHovered(false)}
                    >
                      <XMarkIcon style={{ height: '24px', width: '24px' }} />
                    </button>
                  </div>

                  <form onSubmit={handleEventSubmit} style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
                    {/* Title */}
                    <div>
                      <label htmlFor="eventTitle" style={{ display: 'block', fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.secondary }}>
                        Title *
                      </label>
                      <input
                        id="eventTitle"
                        type="text"
                        style={{
                          marginTop: SPACING[4],
                          display: 'block',
                          width: '100%',
                          borderRadius: RADIUS,
                          border: `1px solid ${COLORS.border.default}`,
                          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                          fontSize: TYPOGRAPHY.size.sm,
                          padding: SPACING[8],
                          fontFamily: TYPOGRAPHY.body.fontFamily,
                          opacity: isSubmittingEvent ? 0.5 : 1,
                        }}
                        placeholder="Event title"
                        value={eventTitle}
                        onChange={(e) => setEventTitle(e.target.value)}
                        disabled={isSubmittingEvent}
                        required
                      />
                    </div>

                    {/* Description */}
                    <div>
                      <label htmlFor="eventDescription" style={{ display: 'block', fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.secondary }}>
                        Description
                      </label>
                      <textarea
                        id="eventDescription"
                        rows={3}
                        style={{
                          marginTop: SPACING[4],
                          display: 'block',
                          width: '100%',
                          borderRadius: RADIUS,
                          border: `1px solid ${COLORS.border.default}`,
                          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                          fontSize: TYPOGRAPHY.size.sm,
                          padding: SPACING[8],
                          fontFamily: TYPOGRAPHY.body.fontFamily,
                          resize: 'vertical',
                          opacity: isSubmittingEvent ? 0.5 : 1,
                        }}
                        placeholder="Event description (optional)"
                        value={eventDescription}
                        onChange={(e) => setEventDescription(e.target.value)}
                        disabled={isSubmittingEvent}
                      />
                    </div>

                    {/* Start Date/Time */}
                    <div>
                      <label htmlFor="eventStart" style={{ display: 'block', fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.secondary }}>
                        Start *
                      </label>
                      <input
                        id="eventStart"
                        type="datetime-local"
                        style={{
                          marginTop: SPACING[4],
                          display: 'block',
                          width: '100%',
                          borderRadius: RADIUS,
                          border: `1px solid ${COLORS.border.default}`,
                          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                          fontSize: TYPOGRAPHY.size.sm,
                          padding: SPACING[8],
                          fontFamily: TYPOGRAPHY.body.fontFamily,
                          opacity: isSubmittingEvent ? 0.5 : 1,
                        }}
                        value={eventStart}
                        onChange={(e) => handleStartTimeChange(e.target.value)}
                        disabled={isSubmittingEvent}
                        required
                      />
                    </div>

                    {/* End Date/Time */}
                    <div>
                      <label htmlFor="eventEnd" style={{ display: 'block', fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.secondary }}>
                        End *
                      </label>
                      <input
                        id="eventEnd"
                        type="datetime-local"
                        style={{
                          marginTop: SPACING[4],
                          display: 'block',
                          width: '100%',
                          borderRadius: RADIUS,
                          border: `1px solid ${COLORS.border.default}`,
                          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                          fontSize: TYPOGRAPHY.size.sm,
                          padding: SPACING[8],
                          fontFamily: TYPOGRAPHY.body.fontFamily,
                          opacity: isSubmittingEvent ? 0.5 : 1,
                        }}
                        value={eventEnd}
                        onChange={(e) => setEventEnd(e.target.value)}
                        disabled={isSubmittingEvent}
                        required
                      />
                    </div>

                    {/* All Day Checkbox */}
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <input
                        id="eventAllDay"
                        type="checkbox"
                        style={{
                          height: '16px',
                          width: '16px',
                          borderRadius: '4px',
                          borderColor: COLORS.border.default,
                          accentColor: COLORS.accent.default,
                          opacity: isSubmittingEvent ? 0.5 : 1,
                        }}
                        checked={eventAllDay}
                        onChange={(e) => setEventAllDay(e.target.checked)}
                        disabled={isSubmittingEvent}
                      />
                      <label htmlFor="eventAllDay" style={{ marginLeft: SPACING[8], display: 'block', fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.primary }}>
                        All day event
                      </label>
                    </div>

                    {/* Error/Success Messages */}
                    {submitEventSuccess && (
                      <div style={{ padding: SPACING[12], fontSize: TYPOGRAPHY.size.sm, color: '#15803D', backgroundColor: '#F0FDF4', borderRadius: RADIUS, border: '1px solid #BBF7D0', display: 'flex', alignItems: 'center' }}>
                        <CheckCircleIcon style={{ height: '20px', width: '20px', marginRight: SPACING[8], flexShrink: 0 }} />
                        Event created successfully!
                      </div>
                    )}
                    {submitEventError && (
                      <div style={{ padding: SPACING[12], fontSize: TYPOGRAPHY.size.sm, color: '#B91C1C', backgroundColor: '#FEF2F2', borderRadius: RADIUS, border: '1px solid #FECACA', display: 'flex', alignItems: 'center' }}>
                        <ExclamationCircleIcon style={{ height: '20px', width: '20px', marginRight: SPACING[8], flexShrink: 0 }} />
                        {submitEventError}
                      </div>
                    )}

                    {/* Submit Button */}
                    <button
                      type="submit"
                      style={{
                        display: 'inline-flex',
                        width: '100%',
                        justifyContent: 'center',
                        alignItems: 'center',
                        borderRadius: RADIUS,
                        border: 'none',
                        backgroundColor: isSubmittingEvent ? COLORS.text.tertiary : (submitEventHovered ? COLORS.accent.hover : COLORS.accent.default),
                        padding: `${SPACING[12]} ${SPACING[16]}`,
                        fontSize: TYPOGRAPHY.size.sm,
                        fontWeight: TYPOGRAPHY.weight.medium,
                        color: COLORS.bg.panel,
                        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                        cursor: isSubmittingEvent ? 'not-allowed' : 'pointer',
                        opacity: isSubmittingEvent ? 0.5 : 1,
                      }}
                      disabled={isSubmittingEvent}
                      onMouseEnter={() => setSubmitEventHovered(true)}
                      onMouseLeave={() => setSubmitEventHovered(false)}
                    >
                      {isSubmittingEvent ? (
                        <>
                          <ArrowPathIcon style={{ width: '16px', height: '16px', marginRight: SPACING[8], animation: 'spin 1s linear infinite' }} />
                          Creating...
                        </>
                      ) : (
                        'Create Event'
                      )}
                    </button>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Chat FAB Button */}
      <button
        onClick={openChatPanel}
        style={{
          position: 'fixed',
          bottom: SPACING[24],
          right: `calc(${SPACING[24]} + 56px + ${SPACING[12]})`,
          display: isChatOpen ? 'none' : 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '56px',
          height: '56px',
          padding: SPACING[12],
          border: 'none',
          borderRadius: '50%',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          color: COLORS.bg.panel,
          backgroundColor: chatFabHovered ? '#7C3AED' : '#8B5CF6',
          cursor: 'pointer',
          outline: 'none',
          transition: 'transform 150ms ease, background-color 150ms ease',
          transform: chatFabHovered ? 'scale(1.1)' : 'scale(1)',
          zIndex: Z_INDEX.dropdown,
        }}
        onMouseEnter={() => setChatFabHovered(true)}
        onMouseLeave={() => setChatFabHovered(false)}
        aria-label="Open Chat"
      >
        <ChatBubbleLeftRightIcon style={{ height: '28px', width: '28px' }} aria-hidden="true" />
      </button>

      {/* Chat Panel */}
      <Transition show={isChatOpen} as={Fragment}>
        <div
          style={{
            position: 'fixed',
            bottom: SPACING[24],
            right: SPACING[24],
            zIndex: Z_INDEX.modal,
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 scale-95 translate-y-4"
            enterTo="opacity-100 scale-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 scale-100 translate-y-0"
            leaveTo="opacity-0 scale-95 translate-y-4"
          >
            <div
              style={{
                width: '400px',
                height: '500px',
                backgroundColor: COLORS.bg.panel,
                borderRadius: '16px',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              {/* Chat Header */}
              <div
                style={{
                  padding: SPACING[12],
                  borderBottom: `1px solid ${COLORS.border.default}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: '#8B5CF6',
                  color: '#fff',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
                  <ChatBubbleLeftRightIcon style={{ height: '20px', width: '20px' }} />
                  <span style={{ fontWeight: TYPOGRAPHY.weight.semibold, fontSize: TYPOGRAPHY.size.sm }}>
                    FloOS Chat
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[4] }}>
                  <button
                    onClick={startNewChat}
                    title="New Chat"
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: SPACING[4],
                      borderRadius: RADIUS,
                      color: '#fff',
                      opacity: 0.8,
                    }}
                  >
                    <PlusIcon style={{ height: '18px', width: '18px' }} />
                  </button>
                  <Link
                    to="/chat"
                    onClick={closeChatPanel}
                    title="Open Full Chat"
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: SPACING[4],
                      borderRadius: RADIUS,
                      color: '#fff',
                      opacity: 0.8,
                      textDecoration: 'none',
                      display: 'flex',
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ height: '18px', width: '18px' }}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                    </svg>
                  </Link>
                  <button
                    onClick={closeChatPanel}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: SPACING[4],
                      borderRadius: RADIUS,
                      color: '#fff',
                      opacity: 0.8,
                    }}
                  >
                    <XMarkIcon style={{ height: '18px', width: '18px' }} />
                  </button>
                </div>
              </div>

              {/* Choice Screen */}
              {showChatChoice && lastConversation && (
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: SPACING[24],
                    gap: SPACING[16],
                  }}
                >
                  <ChatBubbleLeftRightIcon style={{ height: '48px', width: '48px', color: COLORS.text.tertiary }} />
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: TYPOGRAPHY.size.base, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.primary, marginBottom: SPACING[4] }}>
                      Welcome back!
                    </div>
                    <div style={{ fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.secondary }}>
                      Continue your last conversation or start fresh?
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[8], width: '100%', maxWidth: '280px' }}>
                    <button
                      onClick={resumeLastChat}
                      style={{
                        padding: `${SPACING[12]} ${SPACING[16]}`,
                        borderRadius: RADIUS,
                        border: `1px solid ${COLORS.border.default}`,
                        backgroundColor: COLORS.bg.panel,
                        cursor: 'pointer',
                        textAlign: 'left',
                      }}
                    >
                      <div style={{ fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.primary }}>
                        Resume last chat
                      </div>
                      <div style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.tertiary, marginTop: SPACING[4], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {lastConversation.title}
                      </div>
                    </button>
                    <button
                      onClick={startNewChatFromChoice}
                      style={{
                        padding: `${SPACING[12]} ${SPACING[16]}`,
                        borderRadius: RADIUS,
                        border: 'none',
                        backgroundColor: '#8B5CF6',
                        color: '#fff',
                        cursor: 'pointer',
                        fontSize: TYPOGRAPHY.size.sm,
                        fontWeight: TYPOGRAPHY.weight.medium,
                      }}
                    >
                      Start new chat
                    </button>
                  </div>
                </div>
              )}

              {/* Chat Messages */}
              {!showChatChoice && (
              <div
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  padding: SPACING[12],
                  display: 'flex',
                  flexDirection: 'column',
                  gap: SPACING[8],
                }}
              >
                {chatMessages.length === 0 && (
                  <div style={{ textAlign: 'center', color: COLORS.text.tertiary, padding: SPACING[24], fontSize: TYPOGRAPHY.size.sm }}>
                    Ask me anything about your data, tasks, finances, health, or code.
                  </div>
                )}
                {chatMessages.map((msg, idx) => {
                  const isUser = msg.role === 'user';
                  const modelLabel = getModelLabel(msg.model);
                  const isLastAssistant = !isUser && !chatMessages.slice(idx + 1).some(m => m.role === 'assistant');
                  return (
                    <div
                      key={msg.id}
                      style={{
                        alignSelf: isUser ? 'flex-end' : 'flex-start',
                        maxWidth: '85%',
                        padding: `${SPACING[8]} ${SPACING[12]}`,
                        borderRadius: RADIUS,
                        backgroundColor: isUser ? COLORS.accent.default : COLORS.bg.hover,
                        color: isUser ? '#fff' : COLORS.text.primary,
                        fontSize: TYPOGRAPHY.size.sm,
                        whiteSpace: isUser ? 'pre-wrap' : undefined,
                        wordBreak: 'break-word',
                        position: 'relative',
                      }}
                    >
                      {modelLabel && (
                        <span style={{
                          position: 'absolute',
                          top: -6,
                          right: 6,
                          backgroundColor: modelLabel.color,
                          color: '#fff',
                          padding: `1px ${SPACING[4]}`,
                          borderRadius: RADIUS,
                          fontSize: '10px',
                          fontWeight: 600,
                        }}>
                          {modelLabel.name}
                        </span>
                      )}
                      {isUser ? msg.content : (
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={chatMdComponents}>
                          {msg.content}
                        </ReactMarkdown>
                      )}
                      {msg.tool_calls && msg.tool_calls.length > 0 && (
                        <div style={{ marginTop: SPACING[4], fontSize: TYPOGRAPHY.body.sizes.tiny, color: isUser ? 'rgba(255,255,255,0.7)' : COLORS.text.tertiary }}>
                          {msg.tool_calls.length} tool call{msg.tool_calls.length !== 1 ? 's' : ''}
                        </div>
                      )}
                      {isLastAssistant && chatVerification && !chatVerification.verified && (
                        <div style={{ fontSize: 11, color: COLORS.status.warning, marginTop: 4 }}>
                          ⚠ {chatVerification.issues.length} verification issue{chatVerification.issues.length !== 1 ? 's' : ''}
                        </div>
                      )}
                      {isLastAssistant && chatVerification && chatVerification.verified && (
                        <div style={{ fontSize: 11, color: COLORS.status.success, marginTop: 4 }}>
                          ✓ Verified
                        </div>
                      )}
                    </div>
                  );
                })}
                {/* Streaming preview */}
                {chatIsStreaming && chatStreamingContent && (
                  <div
                    style={{
                      alignSelf: 'flex-start',
                      maxWidth: '85%',
                      padding: `${SPACING[8]} ${SPACING[12]}`,
                      borderRadius: RADIUS,
                      backgroundColor: COLORS.bg.hover,
                      color: COLORS.text.primary,
                      fontSize: TYPOGRAPHY.size.sm,
                      wordBreak: 'break-word',
                      position: 'relative',
                    }}
                  >
                    {chatStreamingModel && (() => {
                      const ml = getModelLabel(chatStreamingModel);
                      return ml ? (
                        <span style={{
                          position: 'absolute', top: -6, right: 6,
                          backgroundColor: ml.color, color: '#fff',
                          padding: `1px ${SPACING[4]}`, borderRadius: RADIUS,
                          fontSize: '10px', fontWeight: 600,
                        }}>{ml.name}</span>
                      ) : null;
                    })()}
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={chatMdComponents}>
                      {chatStreamingContent}
                    </ReactMarkdown>
                  </div>
                )}
                {chatLoading && !chatStreamingContent && (
                  <div style={{ alignSelf: 'flex-start', padding: SPACING[8], color: COLORS.text.tertiary, fontSize: TYPOGRAPHY.size.sm, display: 'flex', alignItems: 'center', gap: SPACING[4] }}>
                    <ArrowPathIcon style={{ width: '14px', height: '14px', animation: 'spin 1s linear infinite' }} />
                    Thinking...
                  </div>
                )}
                {/* Confirmation UI for pending write operations */}
                {chatPendingConfirmations.length > 0 && (
                  <div style={{
                    alignSelf: 'stretch',
                    padding: SPACING[12],
                    backgroundColor: 'rgba(245, 158, 11, 0.1)',
                    border: `1px solid ${COLORS.status.warning}`,
                    borderRadius: RADIUS,
                    marginTop: SPACING[8],
                  }}>
                    <div style={{ fontSize: TYPOGRAPHY.size.sm, fontWeight: 500, marginBottom: SPACING[8] }}>
                      Confirm {chatPendingConfirmations.length} write operation{chatPendingConfirmations.length !== 1 ? 's' : ''}:
                    </div>
                    <div style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, color: COLORS.text.secondary, marginBottom: SPACING[8] }}>
                      {chatPendingConfirmations.map(tc => tc.tool_name).join(', ')}
                    </div>
                    <div style={{ display: 'flex', gap: SPACING[8] }}>
                      <button
                        onClick={handleChatConfirmAll}
                        style={{
                          padding: `${SPACING[4]} ${SPACING[12]}`,
                          backgroundColor: COLORS.accent.default,
                          color: '#fff',
                          border: 'none',
                          borderRadius: RADIUS,
                          fontSize: TYPOGRAPHY.body.sizes.small,
                          cursor: 'pointer',
                        }}
                      >
                        Confirm {chatPendingConfirmations.length > 1 ? `All (${chatPendingConfirmations.length})` : ''}
                      </button>
                      <button
                        onClick={handleChatReject}
                        style={{
                          padding: `${SPACING[4]} ${SPACING[12]}`,
                          backgroundColor: COLORS.text.tertiary,
                          color: '#fff',
                          border: 'none',
                          borderRadius: RADIUS,
                          fontSize: TYPOGRAPHY.body.sizes.small,
                          cursor: 'pointer',
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                <div ref={chatMessagesEndRef} />
              </div>
              )}

              {/* Chat Input */}
              {!showChatChoice && (
              <div
                style={{
                  padding: SPACING[12],
                  borderTop: `1px solid ${COLORS.border.default}`,
                  display: 'flex',
                  gap: SPACING[8],
                }}
              >
                <button
                  onClick={() => setChatAgentMode(!chatAgentMode)}
                  title={chatAgentMode ? 'Agent mode ON' : 'Agent mode OFF'}
                  style={{
                    padding: SPACING[4],
                    borderRadius: RADIUS,
                    border: chatAgentMode ? 'none' : `1px solid ${COLORS.border.default}`,
                    backgroundColor: chatAgentMode ? '#8B5CF6' : 'transparent',
                    color: chatAgentMode ? '#fff' : COLORS.text.tertiary,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <BoltIcon style={{ width: '16px', height: '16px' }} />
                </button>
                <input
                  type="text"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={handleChatKeyDown}
                  placeholder="Ask anything..."
                  disabled={chatLoading}
                  style={{
                    flex: 1,
                    padding: `${SPACING[8]} ${SPACING[12]}`,
                    borderRadius: RADIUS,
                    border: `1px solid ${COLORS.border.default}`,
                    fontSize: TYPOGRAPHY.size.sm,
                    fontFamily: TYPOGRAPHY.body.fontFamily,
                    outline: 'none',
                  }}
                />
                <button
                  onClick={() => sendChatMessage()}
                  disabled={chatLoading || !chatInput.trim()}
                  style={{
                    padding: SPACING[8],
                    borderRadius: RADIUS,
                    border: 'none',
                    backgroundColor: (chatLoading || !chatInput.trim()) ? COLORS.text.tertiary : '#8B5CF6',
                    color: '#fff',
                    cursor: (chatLoading || !chatInput.trim()) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <PaperAirplaneIcon style={{ width: '18px', height: '18px' }} />
                </button>
              </div>
              )}
            </div>
          </Transition.Child>
        </div>
      </Transition>
    </>
  );
};

export default FloatingActionMenu;
